/**
 * Hook para buscar dados de origem/mídia das páginas do Google Analytics 4
 */
import { useState, useEffect } from "react";
import { getPagesSourceMediumApiV1PagesSourceMediumGet } from "@/backend-sdk/client/sdk.gen";
import type {
  PagesSourceMediumItem,
  PagesSourceMediumTotal,
} from "@/backend-sdk/client/types.gen";
import { isHeyApiError } from "@/lib/hey-api-client";
import type { TableQueryDTO } from "@/types/table-query";

type PagesSourceMediumData = PagesSourceMediumItem;

export const usePagesSourceMedium = (
  startDate: string,
  endDate: string,
  comparison: string,
  customerUuid?: string,
  accountId?: string,
  page: number = 1,
  pageSize: number = 10,
  tableQuery?: TableQueryDTO,
  campaignFiltersNonce?: number,
) => {
  const [data, setData] = useState<PagesSourceMediumData[]>([]);
  const [total, setTotal] = useState<PagesSourceMediumTotal | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!customerUuid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: response } =
          await getPagesSourceMediumApiV1PagesSourceMediumGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              page,
              page_size: pageSize,
              q: tableQuery?.q,
              sort_by: tableQuery?.sortBy,
              sort_dir: tableQuery?.sortDir,
              filters: tableQuery?.filters?.length
                ? JSON.stringify(tableQuery.filters)
                : undefined,
            },
            throwOnError: true,
          });

        setData(response.items || []);
        setTotal(response.total || null);
        setPagination(
          response.pagination || {
            current_page: 1,
            page_size: 10,
            total_items: 0,
            total_pages: 0,
          },
        );
      } catch (err: unknown) {
        console.error("Erro ao buscar dados de origem/mídia:", err);
        setError(
          isHeyApiError(err) && typeof err.detail === "string"
            ? err.detail
            : "Erro ao buscar dados de origem/mídia",
        );
        setData([]);
        setTotal(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    page,
    pageSize,
    tableQuery,
    campaignFiltersNonce,
  ]);

  return {
    data,
    total,
    pagination,
    loading,
    error,
  };
};
